import React from 'react';
import Icon from '../shared/Icon';
import { distanciaDoEstimado, totalEstimado, fraseDaDistancia, fmtPct } from '../../utils/precoVsEstimado';
import {
  computeItemTotal, computeProposalTotal, CHARGE_TYPE_LABELS, ChargeType,
  PriceMode, isDiscountItem, unitPriceFromDiscount, discountFromUnitPrice,
  MAX_DISCOUNT_PERCENT,
} from '../../utils/proposalMath';
import { campo, campoComEstado } from '../../design';
import MaisAcoes from '../shared/MaisAcoes';
import { useConfirm } from '../shared/ConfirmDialog';

/**
 * Tabela de itens da proposta (aba "Itens & Preços") — extraída do
 * LicitacoesTab na F4. Componente de FOLHA: recebe os itens e devolve o array
 * novo por `onChange`; não busca nada nem guarda estado do formulário.
 *
 * Por que existir: no monólito cada campo repetia o padrão
 * `const updated = [...form]; updated[idx] = {...}; setForm(updated)` — 8×.
 * Aqui isso é UMA função (`patch`), e a aritmética de total vem do
 * proposalMath (espelho do backend), nunca de conta inline.
 */

export interface ProposalItemForm {
  itemNumber: number;
  description: string;
  descricaoDetalhada?: string;
  quantity: number;
  unit: string;
  estimatedPrice: number;
  valorEstimadoSigiloso?: boolean;
  unitPrice: string;
  brand: string;
  model: string;
  groupNumber?: number;
  groupDescription?: string;
  requiresBrandModel?: boolean;
  requiresModel?: boolean;
  lote?: number;
  catser?: string;
  chargeType?: ChargeType;
  maxUnitPrice?: string;
  /** tratamento diferenciado do portal (Art. 4º) — informativo, espelha o ComprasNet */
  tipoBeneficio?: number;
  tipoBeneficioNome?: string;
  /**
   * Como o PORTAL pede o preço DESTE item (critério de julgamento do item):
   * 'desconto' = campo "Desconto (%)" e o unitário sai derivado do estimado.
   * Vem do scrape — o operador não escolhe. Ver utils/proposalMath.ts.
   */
  priceMode?: PriceMode;
  /** O percentual que vai pro portal quando priceMode='desconto'. */
  discountPercent?: string;
}

interface Props {
  items: ProposalItemForm[];
  onChange: (items: ProposalItemForm[]) => void;
  contractMonths: number;
  onContractMonthsChange: (v: string) => void;
  fmt: (v: any) => string;
  catserOptions: { code: string; label: string }[];
  /** só aparece com proposta salva + itens tipados (precisa de proposalId no backend) */
  canAdequar: boolean;
  /** altura máxima do corpo da tabela; na página de trabalho vale a tela toda */
  maxHeightClass?: string;
  /**
   * Como o PORTAL chama o agrupamento. BLL e BNC dizem "LOTE"; ComprasNet diz
   * "GRUPO". Chamar de grupo o que o edital chama de lote fez o operador achar
   * que os 3 lotes do BLL não tinham sido capturados (Nova Olímpia 021/2026).
   */
  groupLabel?: string;
  onAdequar: () => void;
  onClear: () => void;
}

/**
 * Tratamento diferenciado do item (Art. 4º da Lei 14.133) exatamente como o
 * ComprasNet mostra. É INFORMATIVO: não muda a declaração ME/EPP da empresa.
 * tipoBeneficio do PNCP: 1=exclusivo ME/EPP, 2=subcontratação, 3=cota reservada,
 * 4=sem benefício.
 */
function beneficioLabel(it: { tipoBeneficio?: number; tipoBeneficioNome?: string }): string | null {
  if (it.tipoBeneficioNome) return it.tipoBeneficioNome;
  switch (it.tipoBeneficio) {
    case 1: return 'Exclusivo ME/EPP';
    case 2: return 'Subcontratação ME/EPP';
    case 3: return 'Cota reservada ME/EPP';
    case 4: return 'Sem benefícios ME/EPP';
    case 5: return 'Sem benefícios ME/EPP';
    default: return null;
  }
}

export default function ProposalItemsTable({
  items, onChange, contractMonths, onContractMonthsChange, fmt, catserOptions,
  canAdequar, onAdequar, onClear, maxHeightClass = 'max-h-[40vh]',
  groupLabel = 'GRUPO',
}: Props) {
  const confirm = useConfirm();
  const patch = (idx: number, fields: Partial<ProposalItemForm>) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], ...fields };
    onChange(updated);
  };

  // Linhas com os campos CATSER/tipo/máx abertos (M3) e com a descrição detalhada inteira (M4), pelo índice.
  const [detalheAberto, setDetalheAberto] = React.useState<Set<number>>(() => new Set());
  const [descAberta, setDescAberta] = React.useState<Set<number>>(() => new Set());
  const alternar = (set: React.Dispatch<React.SetStateAction<Set<number>>>, idx: number) =>
    set((prev) => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  // Descrição detalhada até este tamanho cabe nas 2 linhas; acima, ganha o "ver descrição inteira".
  const DESCRICAO_CURTA = 140;

  /**
   * Preencheu = tem oferta. Em item de MAIOR DESCONTO quem diz isso é o
   * PERCENTUAL, não o unitário derivado: 0% é ofertar o próprio valor estimado
   * e 100% zeraria o unitário — os dois são ofertas. O que não é oferta é o
   * campo em branco. Olhar só o `unitPrice` mentiria nas duas pontas.
   */
  const itemPreenchido = (it: ProposalItemForm) =>
    isDiscountItem(it)
      ? it.discountPercent != null && it.discountPercent !== ''
      : parseFloat(it.unitPrice) > 0;

  /** Muda o desconto e RE-DERIVA o preço, que é o que o portal vai calcular. */
  const patchDiscount = (idx: number, item: ProposalItemForm, raw: string) => {
    // Sem clamp de propósito: o valor fora da faixa fica visível em vermelho.
    // Cortar em 100 enquanto se digita esconderia justamente o erro que este
    // suporte existe para pegar (o operador digitando o preço em reais).
    patch(idx, {
      discountPercent: raw,
      unitPrice: raw === '' ? '' : String(unitPriceFromDiscount(item.estimatedPrice, raw)),
    });
  };

  /** Caminho inverso: digita em R$ e o percentual equivalente vai junto. */
  const patchDiscountFromPrice = (idx: number, item: ProposalItemForm, raw: string) => {
    if (raw === '') return patch(idx, { discountPercent: '', unitPrice: '' });
    const d = discountFromUnitPrice(item.estimatedPrice, raw);
    // Devolve o DERIVADO, não o digitado: o portal só aceita 2 casas no
    // percentual, então R$ 300,00 vira R$ 299,9851. Melhor ver isso agora do
    // que descobrir na planilha do pregoeiro.
    patch(idx, {
      discountPercent: String(d),
      unitPrice: String(unitPriceFromDiscount(item.estimatedPrice, d)),
    });
  };

  // O scrape marca requiresBrandModel E requiresModel juntos (item de MATERIAL,
  // tipoItemCatalogo === 'M'); olhar só uma escondia os campos justamente nos
  // itens em que o portal os exige. Ver [[disputa-trt11-af-grupo-2026-08-28]].
  const exigeMarcaModelo = (it: any) => it.requiresBrandModel === true || it.requiresModel === true;
  const showBM = items.some(exigeMarcaModelo);
  const hasDiscountItem = items.some(isDiscountItem);
  const filledCount = items.filter(itemPreenchido).length;
  const totalCount = items.length;
  const temEstimado = items.some((it) => Number(it.estimatedPrice) > 0);
  const hasTipos = items.some((it) => it.chargeType);
  const hasRecorrente = items.some((it) => it.chargeType === 'recorrente_mensal');
  const total = computeProposalTotal(items, contractMonths);
  const comparacao = totalEstimado(items, contractMonths, total);
  const distinctLotes = Array.from(
    new Set(items.map((it) => it.lote).filter((l) => l != null)),
  ).sort((a: any, b: any) => Number(a) - Number(b)) as number[];
  const colSpan = showBM ? 10 : 8;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-slate-300 font-medium">Itens da Proposta ({totalCount})</label>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${filledCount === totalCount ? 'bg-slate-600/30 text-slate-300' : 'bg-amber-500/20 text-amber-400'}`}>
            {filledCount}/{totalCount} preenchidos
          </span>
          {distinctLotes.length > 1 && (
            <div className="flex items-center gap-1 text-[10px]">
              <span className="text-slate-400">Participar de:</span>
              {distinctLotes.map((lote) => {
                const itemsDoLote = items.filter((it) => it.lote === lote);
                const allZeroed = itemsDoLote.every((it) => it.unitPrice === '' || parseFloat(it.unitPrice) === 0);
                const isOut = allZeroed && itemsDoLote.some((it) => it.unitPrice === '0' || it.unitPrice === '0.00');
                return (
                  <button
                    key={lote}
                    type="button"
                    onClick={() => {
                      onChange(items.map((it) => {
                        if (it.lote !== lote) return it;
                        if (isOut) {
                          return (it.unitPrice === '0' || it.unitPrice === '0.00') ? { ...it, unitPrice: '' } : it;
                        }
                        return { ...it, unitPrice: '0' };
                      }));
                    }}
                    className={`px-2 py-0.5 rounded-full transition-colors ${isOut ? 'bg-central-elevado text-slate-500 line-through' : 'bg-slate-600/30 text-slate-300 hover:bg-central-elevado/50'}`}
                    title={isOut ? `Lote ${lote} excluido (todos itens em 0). Clique para reativar.` : `Lote ${lote} ativo. Clique para excluir todos os itens deste lote.`}
                  >
                    Lote {lote}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasRecorrente && (
            <label className="flex items-center gap-1 text-[10px] text-slate-400 whitespace-nowrap" title="Nº de meses do contrato — itens recorrentes multiplicam o mensal por esse valor no total e no PDF">
              Meses:
              <input
                type="number"
                min="1"
                value={contractMonths}
                onChange={(e) => onContractMonthsChange(e.target.value)}
                className={`w-12 rounded px-1 py-0.5 text-right text-[10px] outline-none ${campo}`}
              />
            </label>
          )}
          {canAdequar && hasTipos && (
            <button
              type="button"
              title="Rateia o lance final entre os itens proporcionalmente aos máximos do edital (sem majorar), com prova de soma — pra proposta ADEQUADA pós-disputa"
              className="text-[10px] border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto px-2 py-1 rounded transition-colors whitespace-nowrap font-medium"
              onClick={onAdequar}
            >⚡ Adequar ao lance final</button>
          )}
          {/* 21/10/2026 (Heitor): é a ação mais usada da montagem. Era um laranja translúcido de 10px, o tom de
              aviso da casa, e lia como alerta; agora é o botão de aplicar, com o nome inteiro. */}
          <button
            type="button"
            data-usar-estimado
            disabled={!temEstimado}
            title={temEstimado
              ? 'Põe o valor estimado como preço em todos os itens que têm estimado (nos itens por desconto, 0%). Item sigiloso ou sem estimado fica como está.'
              : 'Nenhum item tem valor estimado: sincronize do portal antes'}
            className="text-xs bg-primary hover:bg-primary-strong text-white font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => onChange(items.map((it) => {
              if (!it.estimatedPrice) return it;
              // Ofertar o valor estimado num item de maior desconto é ofertar
              // 0% — e o percentual precisa ir junto, senão o envio mandaria
              // reais no campo que o portal lê como porcentagem.
              if (isDiscountItem(it)) {
                return { ...it, discountPercent: '0', unitPrice: String(it.estimatedPrice) };
              }
              return { ...it, unitPrice: String(it.estimatedPrice) };
            }))}
          >Usar o estimado em todos</button>
          {/* Raras e destrutiva no "⋯" (§8). O Limpar apagava todos os itens num clique, sem pergunta, a um botão
              do "Usar o estimado". */}
          <MaisAcoes
            title="Mais ações dos itens"
            itens={[
              {
                label: '+ Adicionar item',
                onClick: () => {
                  const nextNum = items.length > 0 ? Math.max(...items.map((it) => it.itemNumber || 0)) + 1 : 1;
                  onChange([...items, {
                    itemNumber: nextNum, description: '', quantity: 1, unit: 'UN',
                    estimatedPrice: 0, unitPrice: '', brand: '', model: '',
                  }]);
                },
              },
              {
                label: 'Limpar todos os itens…',
                danger: true,
                separadoAntes: true,
                disabled: totalCount === 0,
                onClick: async () => {
                  const ok = await confirm({
                    title: totalCount === 1 ? 'Apagar o item desta proposta?' : `Apagar os ${totalCount} itens desta proposta?`,
                    consequence: 'Os itens e os preços digitados somem da tela. Só ficam apagados de vez quando a proposta for salva.',
                    danger: true,
                    irreversivel: false,
                    confirmLabel: 'Limpar itens',
                  });
                  if (ok) onClear();
                },
              },
            ]}
          />
        </div>
      </div>

      <datalist id="catser-telecom">
        {catserOptions.map((c) => (
          <option key={c.code} value={c.code}>{`${c.code} — ${c.label}`}</option>
        ))}
      </datalist>

      <div className={`overflow-x-auto ${maxHeightClass} overflow-y-auto rounded-lg border border-central-borda`}>
        <table className="w-full text-xs">
          <thead className="bg-central-elevado sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 text-left text-slate-400 font-medium w-8">#</th>
              <th className="px-2 py-2 text-left text-slate-400 font-medium">Descricao</th>
              <th className="px-2 py-2 text-right text-slate-400 font-medium w-20">Qtd</th>
              <th className="px-2 py-2 text-left text-slate-400 font-medium w-20">Unid</th>
              <th className="px-2 py-2 text-right text-slate-400 font-medium w-24">Valor Est.</th>
              <th className="px-2 py-2 text-right text-slate-400 font-medium w-32">
                {hasDiscountItem ? 'Desconto / Preco Unit. *' : 'Preco Unit. *'}
              </th>
              {showBM && (
                <>
                  <th className="px-2 py-2 text-left text-slate-400 font-medium w-24">Marca *</th>
                  <th className="px-2 py-2 text-left text-slate-400 font-medium w-24">Modelo</th>
                </>
              )}
              <th className="px-2 py-2 text-right text-slate-400 font-medium w-28">Total</th>
              <th className="px-2 py-2 text-center text-slate-400 font-medium w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-central-borda">
            {items.map((item, idx) => {
              const { mensal: lineMensal, total: lineTotal } = computeItemTotal(item, contractMonths);
              const isFilled = itemPreenchido(item);
              const maxNum = parseFloat(String(item.maxUnitPrice ?? '')) || 0;
              const overMax = maxNum > 0 && (parseFloat(item.unitPrice) || 0) > maxNum + 1e-9;
              const porDesconto = isDiscountItem(item);
              const descontoNum = parseFloat(item.discountPercent ?? '');
              // É ESTE o erro que gerou o 422: preço em reais num campo de
              // percentual. 351,6 não é "351,6% de desconto", é R$ 351,60.
              const overDesconto =
                porDesconto && Number.isFinite(descontoNum) && descontoNum > MAX_DISCOUNT_PERCENT;
              // Orçamento sigiloso + maior desconto = não há base para derivar
              // o preço. O portal aceita o percentual assim mesmo; o que não dá
              // é mostrar um R$ que não existe.
              const semBaseDesconto = porDesconto && !(Number(item.estimatedPrice) > 0);
              const anyGrouped = items.some((it) => it.groupNumber != null);
              const showGroupHeader = item.groupNumber != null && (idx === 0 || items[idx - 1]?.groupNumber !== item.groupNumber);
              const showAvulsoHeader = item.groupNumber == null && anyGrouped && (idx === 0 || items[idx - 1]?.groupNumber != null);
              const showLoteHeader = item.lote != null && (idx === 0 || items[idx - 1]?.lote !== item.lote);
              return (
                <React.Fragment key={idx}>
                  {showLoteHeader && distinctLotes.length > 1 && (
                    <tr className="bg-central-elevado">
                      <td colSpan={colSpan} className="px-2 py-1.5">
                        <span className="text-[10px] font-bold text-slate-200 uppercase">LOTE {item.lote}</span>
                        <span className="text-[10px] text-slate-500 ml-2 normal-case font-normal">
                          — deixe o valor em 0,00 para não participar deste lote
                        </span>
                      </td>
                    </tr>
                  )}
                  {showGroupHeader && (
                    <tr className="bg-central-elevado">
                      <td colSpan={colSpan} className="px-2 py-1.5">
                        <span className="text-[10px] font-bold text-slate-200 uppercase">
                          {groupLabel} {item.groupNumber}
                        </span>
                        {/* "Por Item" e afins são o MODO DE DISPUTA do lote, não
                            o nome dele. Colado no título ("LOTE 1 — Por Item")
                            lia como se fosse a descrição do lote. */}
                        {item.groupDescription && (
                          <span className="text-[10px] text-slate-500 ml-2 normal-case font-normal">
                            disputa {item.groupDescription.toLowerCase()}
                          </span>
                        )}
                        {(() => {
                          const doGrupo = items.filter((x) => x.groupNumber === item.groupNumber);
                          const b = beneficioLabel(doGrupo[0] || {});
                          const todosIguais = b && doGrupo.every((x) => beneficioLabel(x) === b);
                          return todosIguais ? (
                            <span className="text-[10px] text-slate-400 ml-2 normal-case font-normal">{b}</span>
                          ) : null;
                        })()}
                      </td>
                    </tr>
                  )}
                  {showAvulsoHeader && (
                    <tr className="bg-central-elevado">
                      <td colSpan={colSpan} className="px-2 py-1.5">
                        <span className="text-[10px] font-bold text-slate-200 uppercase">Itens avulsos (fora de grupo)</span>
                      </td>
                    </tr>
                  )}
                  <tr className={`transition-colors ${isFilled ? 'hover:bg-central-elevado' : 'hover:bg-central-elevado'}`}>
                    <td className="px-2 py-1.5 text-slate-500">{item.itemNumber}</td>
                    <td className="px-2 py-1.5">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => patch(idx, { description: e.target.value })}
                        className="w-full bg-transparent border-0 border-b border-central-borda focus:border-primary text-slate-300 text-xs outline-none px-0 py-0.5 transition-colors"
                        placeholder="Descrição do item"
                        title={item.descricaoDetalhada || item.description}
                      />
                      {item.descricaoDetalhada && (
                        <div className={`text-[10px] text-slate-500 italic mt-0.5 leading-tight ${descAberta.has(idx) ? 'whitespace-pre-line' : 'line-clamp-2'}`} title={item.descricaoDetalhada} data-descricao-detalhada>
                          {item.descricaoDetalhada}
                        </div>
                      )}
                      {/* M4 (21/10/2026): é nessa descrição que está o que se precifica (no CRMV-PE 66/2026, os 50 mil e-mails
                          e os créditos de WhatsApp), e ela ficava cortada em 2 linhas, com o resto só no tooltip. */}
                      {(item.descricaoDetalhada?.length ?? 0) > DESCRICAO_CURTA && (
                        <button
                          type="button"
                          onClick={() => alternar(setDescAberta, idx)}
                          className="text-[10px] text-slate-400 underline hover:text-white mt-0.5"
                        >{descAberta.has(idx) ? 'recolher descrição' : 'ver descrição inteira'}</button>
                      )}
                      {beneficioLabel(item) && (
                        <div
                          className="text-[10px] text-slate-400 mt-0.5"
                          title="Tratamento diferenciado do item (Art. 4º da Lei 14.133/2021), como o portal informa. É informativo — não altera a declaração ME/EPP da empresa."
                        >
                          {beneficioLabel(item)}
                        </div>
                      )}
                      {porDesconto && (
                        <div
                          className="inline-block text-[9px] text-slate-300 bg-slate-500/15 border border-central-borda rounded px-1.5 py-0.5 mt-0.5"
                          title="Critério de julgamento DESTE item no portal: Maior Desconto. O ComprasNet pede um percentual sobre o valor estimado e calcula o preço unitário — numa mesma compra pode haver item por preço e item por desconto."
                        >
                          por desconto (%)
                        </div>
                      )}
                      {/* M3 (21/10/2026): os três campos de 10px ficavam abertos em todo item, quase sempre vazios.
                          Fechado, mostra só o que está preenchido; o ✎ abre os campos. O máximo segue à vista porque é
                          ele que acusa o "acima do máx!". */}
                      {!detalheAberto.has(idx) ? (
                        <div className="flex items-center gap-1 mt-1 flex-wrap" data-etiquetas-do-item>
                          {item.catser && (
                            <span className="text-[10px] text-slate-400 bg-central-cartao border border-central-borda rounded px-1.5" title="Código CATSER">CATSER {item.catser}</span>
                          )}
                          {item.chargeType && (
                            <span className="text-[10px] text-slate-400 bg-central-cartao border border-central-borda rounded px-1.5" title="Tipo de cobrança do item">{CHARGE_TYPE_LABELS[item.chargeType]}</span>
                          )}
                          {maxNum > 0 && (
                            <span
                              className={`text-[10px] bg-central-cartao border rounded px-1.5 ${overMax ? 'text-red-400 border-red-500/60' : 'text-slate-400 border-central-borda'}`}
                              title="Preço unitário MÁXIMO do edital. Usado na validação e no Adequar ao lance final."
                            >Máx {fmt(maxNum)}{Math.abs(maxNum - Number(item.estimatedPrice)) < 0.00005 ? ' (= est.)' : ''}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => alternar(setDetalheAberto, idx)}
                            className="text-[10px] text-slate-500 hover:text-slate-300"
                            title="Editar CATSER, tipo de cobrança e máximo do edital"
                          >✎ CATSER, tipo, máx</button>
                        </div>
                      ) : (
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <span className="text-[9px] text-slate-500 uppercase">CATSER:</span>
                        <input
                          type="text"
                          list="catser-telecom"
                          value={item.catser || ''}
                          onChange={(e) => patch(idx, { catser: e.target.value })}
                          className="w-28 bg-transparent border-b border-central-borda focus:border-primary text-slate-400 text-[10px] outline-none px-0 py-0 transition-colors"
                          placeholder="código (sugestões)"
                          title="Código CATSER (Catálogo de Serviços gov.br). Comece a digitar pra ver sugestões de telecom."
                        />
                        <span className="text-[9px] text-slate-500 uppercase ml-1">Tipo:</span>
                        <select
                          value={item.chargeType || ''}
                          onChange={(e) => patch(idx, { chargeType: (e.target.value || undefined) as ChargeType | undefined })}
                          className={`rounded px-1 py-0 text-[10px] outline-none transition-colors ${item.chargeType ? campoComEstado(null, 'text-white') : campoComEstado(null, 'text-central-texto-fraco')}`}
                          title="Tipo de cobrança do item: Recorrente mensal (total = mensal × meses do contrato), Taxa única (valor único, qtd ignorada) ou Aquisição (unit × qtd, padrão do portal). Vazio = cálculo legado unit × qtd."
                        >
                          <option value="">padrão (unit×qtd)</option>
                          {Object.entries(CHARGE_TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                        <span className="text-[9px] text-slate-500 uppercase ml-1">Máx:</span>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={item.maxUnitPrice ?? ''}
                          onChange={(e) => patch(idx, { maxUnitPrice: e.target.value || undefined })}
                          className="w-20 bg-transparent border-b border-central-borda focus:border-primary text-slate-400 text-[10px] outline-none px-0 py-0 transition-colors text-right"
                          placeholder="teto edital"
                          title="Preço unitário MÁXIMO do edital (4 casas). Usado na validação e no Adequar ao lance final."
                        />
                        <button
                          type="button"
                          onClick={() => alternar(setDetalheAberto, idx)}
                          className="text-[10px] text-slate-500 hover:text-slate-300 ml-1"
                        >concluir</button>
                      </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => patch(idx, { quantity: parseFloat(e.target.value) || 1 })}
                        className="w-full bg-transparent border-0 border-b border-central-borda focus:border-primary text-right text-slate-300 text-xs outline-none px-0 py-0.5 transition-colors"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => patch(idx, { unit: e.target.value })}
                        className="w-full bg-transparent border-0 border-b border-central-borda focus:border-primary text-slate-400 text-[10px] outline-none px-0 py-0.5 transition-colors"
                        placeholder="UN"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {item.valorEstimadoSigiloso ? (
                        <span
                          className="inline-block text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5"
                          title="Valor estimado é sigiloso — defina o preço manualmente"
                        >🔒 Sigiloso</span>
                      ) : (
                        <button
                          type="button"
                          title={porDesconto
                            ? 'Clique para ofertar o próprio valor estimado — que neste item é desconto de 0%.'
                            : 'Clique para usar como Preco Unit.'}
                          className="text-slate-400 hover:text-white cursor-pointer transition-colors text-xs hover:bg-central-elevado px-1 py-0.5 rounded"
                          onClick={() => {
                            if (!item.estimatedPrice) return;
                            // Em item de desconto, "usar o estimado" é 0% — e o
                            // percentual precisa ir junto, senão o portal receberia
                            // o preço em reais no campo errado.
                            if (porDesconto) patchDiscount(idx, item, '0');
                            else patch(idx, { unitPrice: String(item.estimatedPrice) });
                          }}
                        >{fmt(item.estimatedPrice)} &rarr;</button>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {porDesconto ? (
                        <>
                          {/* O campo que o portal realmente recebe. O preço
                              unitário logo abaixo é DERIVADO — é o que o
                              ComprasNet calcula a partir do valor estimado. */}
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={MAX_DISCOUNT_PERCENT}
                              value={item.discountPercent ?? ''}
                              onChange={(e) => patchDiscount(idx, item, e.target.value)}
                              className={`w-full border rounded pl-2 pr-6 py-1 text-right text-white text-xs outline-none transition-colors ${overDesconto ? 'bg-red-900/40 border-red-500/70 focus:border-red-400 focus:ring-1 focus:ring-red-400' : isFilled ? 'bg-central-elevado border-central-borda focus:border-primary focus:ring-1 focus:ring-primary' : 'bg-central-elevado border-slate-500 focus:border-primary focus:ring-1 focus:ring-primary'}`}
                              placeholder="0,00"
                              title={`Item julgado por MAIOR DESCONTO: o portal pede DESCONTO (%) sobre o valor estimado, não o preço. Máximo ${MAX_DISCOUNT_PERCENT}%, 2 casas.`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">%</span>
                          </div>
                          {overDesconto ? (
                            <div className="text-[9px] text-red-400 mt-0.5 text-right leading-tight">
                              desconto &gt; {MAX_DISCOUNT_PERCENT}% — isto é preço, não percentual
                            </div>
                          ) : semBaseDesconto ? (
                            <div className="text-[9px] text-amber-400/80 mt-0.5 text-right leading-tight">
                              sem valor estimado: o portal calcula o unitário
                            </div>
                          ) : (
                            <div className="relative mt-1">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none">R$</span>
                              <input
                                type="number"
                                step="0.0001"
                                min="0"
                                value={item.unitPrice}
                                onChange={(e) => patchDiscountFromPrice(idx, item, e.target.value)}
                                className={`w-full bg-transparent border rounded pl-7 pr-2 py-0.5 text-right text-[11px] outline-none transition-colors ${overMax ? 'border-red-500/70 text-red-300' : 'border-central-borda text-slate-300 focus:border-primary'}`}
                                placeholder="unitário"
                                title="Preço unitário resultante do desconto. Dá pra digitar aqui: o percentual equivalente é calculado — e como o portal só aceita 2 casas no desconto, o valor volta ajustado."
                              />
                            </div>
                          )}
                          {overMax && !overDesconto && (
                            <div className="text-[9px] text-red-400 mt-0.5 text-right">acima do máx!</div>
                          )}
                        </>
                      ) : (
                        <>
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={item.unitPrice}
                            onChange={(e) => patch(idx, { unitPrice: e.target.value })}
                            className={`w-full border rounded px-2 py-1 text-right text-white text-xs outline-none transition-colors ${overMax ? 'bg-red-900/40 border-red-500/70 focus:border-red-400 focus:ring-1 focus:ring-red-400' : isFilled ? 'bg-central-elevado border-central-borda focus:border-primary focus:ring-1 focus:ring-primary' : 'bg-central-elevado border-slate-500 focus:border-primary focus:ring-1 focus:ring-primary'}`}
                            placeholder="0,00"
                            title={overMax ? `⚠️ ACIMA do máximo do edital (${item.maxUnitPrice}) — risco de desclassificação` : undefined}
                          />
                          {overMax && <div className="text-[9px] text-red-400 mt-0.5 text-right">acima do máx!</div>}
                          {/* M1 (21/10/2026): a comparação com a coluna Valor Est., que se fazia de olho. Acima do máximo
                              o vermelho já fala; item por desconto já mostra o percentual. */}
                          {!overMax && (() => {
                            const d = distanciaDoEstimado(item);
                            return d ? (
                              <div className={`text-[9px] mt-0.5 text-right ${d.tipo === 'igual' ? 'text-slate-400' : d.tipo === 'acima' ? 'text-amber-400' : 'text-slate-400'}`} data-distancia-do-estimado>
                                {fraseDaDistancia(d)}
                              </div>
                            ) : null;
                          })()}
                        </>
                      )}
                    </td>
                    {showBM && (
                      <>
                        <td className="px-2 py-1.5">
                          {exigeMarcaModelo(item) ? (
                            <input
                              type="text"
                              value={item.brand}
                              onChange={(e) => patch(idx, { brand: e.target.value })}
                              className="w-full bg-central-elevado border border-slate-500 rounded px-2 py-1 text-white text-xs focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              placeholder="Marca"
                            />
                          ) : <span className="text-slate-600 text-[10px]">N/A</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {exigeMarcaModelo(item) ? (
                            <input
                              type="text"
                              value={item.model}
                              onChange={(e) => patch(idx, { model: e.target.value })}
                              className="w-full bg-central-elevado border border-slate-500 rounded px-2 py-1 text-white text-xs focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              placeholder="Modelo"
                            />
                          ) : <span className="text-slate-600 text-[10px]">N/A</span>}
                        </td>
                      </>
                    )}
                    <td className="px-2 py-1.5 text-right text-white font-medium">
                      {lineTotal > 0 ? fmt(lineTotal) : '—'}
                      {item.chargeType === 'recorrente_mensal' && lineMensal != null && lineMensal > 0 && (
                        <div className="text-[9px] text-slate-500 font-normal">{fmt(lineMensal)}/mês ×{contractMonths}</div>
                      )}
                      {item.chargeType === 'taxa_unica' && lineTotal > 0 && (
                        <div className="text-[9px] text-slate-500 font-normal">taxa única</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => onChange(items.filter((_, i) => i !== idx))}
                        className="p-0.5 text-slate-500 hover:text-red-400 transition-colors"
                        title="Remover item"
                      >
                        <Icon name="trash" className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-central-elevado sticky bottom-0">
            <tr>
              <td colSpan={showBM ? 9 : 7} className="px-2 py-2 text-right text-sm text-slate-300 font-medium">
                Total Geral:
                {/* M2 (21/10/2026): quanto a proposta inteira está do total estimado do órgão. */}
                {total > 0 && (
                  <div className="text-[10px] text-slate-400 font-normal" data-total-vs-estimado>
                    {comparacao.distancia
                      ? `${comparacao.distancia.tipo === 'igual' ? 'igual ao' : `${fmtPct(comparacao.distancia.pct)} ${comparacao.distancia.tipo} do`} estimado (${fmt(comparacao.estimado)})`
                      : comparacao.semEstimado > 0
                        ? `${comparacao.semEstimado} ${comparacao.semEstimado === 1 ? 'item sem' : 'itens sem'} valor estimado: sem comparação com o total do órgão`
                        : null}
                  </div>
                )}
              </td>
              <td className="px-2 py-2 text-right text-sm text-white font-bold">{fmt(total)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
